import { ApiPath } from '@common/app/api-path.ts';
import {
  type ClaimTaskRewardRequestType,
  type ClaimTaskRewardResponseType,
  type PartnerDto,
  type PartnerTaskDto,
} from '@common/types/query/tasks.ts';
import {
  type ActivateVipStatusBuyLinkResponseType,
  type ActivateVipStatusResponseType,
  type ActiveteVipStatusRequestType,
  type ChangeAdditionalParamsType,
  type ClaimDailyTgemBonuseRequestType,
  type ClaimDailyTgemBonuseResponseType,
  type EnergyRefillResponseType,
  type GetUserInviteFriendsRequestType,
  type ThankForPurchaseRequestResponseType,
  type UserDecorationsResponseType,
  type UserInviteFriendType,
  type UserReferralEarningsType,
  type VipInfoResponseType,
  type WithdrawHoldedMoneyResponseType,
} from '@common/types/query/user.ts';
import { type UserTaskType } from '@common/types/user/index.ts';

import { type Airdrop } from '@/common/types/airdrop/index.ts';
import { type PaginationRequestType } from '@/common/types/common.ts';
import {
  type GetTonTransactionsResponseType,
  type GetUserPartnersRequestType,
  type GetUserPartnersResponseType,
  type InitialRewardsResponse,
  type OnboardingRewardsRequest,
  type OnboardingRewardsResponse,
  type User,
  type UserExtendedInfoType,
} from '@/common/types/userType.ts';

import { api } from '../base.ts';

const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    authTelegram: builder.mutation<User, void>({
      query: () => ({
        url: ApiPath.AUTH_TELEGRAM,
        method: 'POST',
        headers: {
          'X-TG-INIT': Telegram?.WebApp?.initData,
        },
      }),
    }),
    getMyInfo: builder.mutation<User, void>({
      query: () => ({
        url: ApiPath.AUTH_USER,
        method: 'POST',
      }),
    }),
    getInitialRewards: builder.mutation<InitialRewardsResponse, void>({
      query: () => ({
        url: ApiPath.INITIAL_REWARDS,
        method: 'POST',
      }),
    }),
    getOnboardingRewards: builder.mutation<
      OnboardingRewardsResponse,
      OnboardingRewardsRequest
    >({
      query: (body) => ({
        url: ApiPath.ONBOARDING_REWARDS,
        method: 'POST',
        body,
      }),
    }),
    extendedInfo: builder.query<UserExtendedInfoType, void>({
      query: () => ApiPath.EXTENDED_INFO,
      keepUnusedDataFor: 60,
    }),
    getUserTasks: builder.query<UserTaskType[], void>({
      query: () => ApiPath.GET_USER_TASKS,
      keepUnusedDataFor: 0,
    }),
    changeAdditionalParams: builder.mutation<
      ChangeAdditionalParamsType,
      ChangeAdditionalParamsType
    >({
      query: (body) => ({
        url: ApiPath.USER,
        method: 'PATCH',
        body,
      }),
    }),
    // Инвентарь скинов профиля. Переключатель в настройках показываем
    // только тем, у кого выиграно больше одного.
    getUserDecorations: builder.query<UserDecorationsResponseType, void>({
      query: () => ApiPath.USER_DECORATIONS,
      providesTags: ['UserDecorations'],
    }),
    selectUserDecoration: builder.mutation<UserDecorationsResponseType, string>(
      {
        query: (decorationId) => ({
          url: ApiPath.SELECT_USER_DECORATION,
          method: 'POST',
          body: { decorationId },
        }),
        invalidatesTags: ['UserDecorations'],
      },
    ),
    claimTaskById: builder.mutation<
      ClaimTaskRewardResponseType,
      ClaimTaskRewardRequestType
    >({
      query: ({ taskId }) => ({
        url: ApiPath.CLAIM_TASK + taskId,
        method: 'POST',
      }),
    }),
    getReferralEarnings: builder.query<UserReferralEarningsType, void>({
      query: () => ApiPath.REFERRAL_EARNINGS,
      keepUnusedDataFor: 0,
    }),
    getUserInviteFriendsList: builder.query<
      UserInviteFriendType[],
      GetUserInviteFriendsRequestType
    >({
      query: (params) => ({
        url: ApiPath.INVITED_FRIENDS,
        params,
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        endpointName + queryArgs.referral_level,
      merge: (currentCache, newItems) => {
        const existingIds = new Set(
          currentCache.map((item) => item.telegramId),
        );
        newItems.forEach((item) => {
          if (!existingIds.has(item.telegramId)) {
            currentCache.push(item); // Додаємо лише унікальні записи
          }
        });
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
    }),
    withdrawHoldedMoney: builder.mutation<
      WithdrawHoldedMoneyResponseType,
      void
    >({
      query: () => ({
        url: ApiPath.WITHDRAW_HOLDED_MONEY,
        method: 'POST',
      }),
    }),
    getPartnersTask: builder.query<PartnerDto[], void>({
      query: () => ApiPath.PARTNERS,
      keepUnusedDataFor: 0,
    }),
    completePartnerTask: builder.mutation<PartnerTaskDto, { taskId: string }>({
      query: ({ taskId }) => ({
        url: ApiPath.PARTNERS + `/tasks/${taskId}/complete`,
        method: 'POST',
      }),
    }),
    getPartnerTaskReward: builder.mutation<PartnerTaskDto, { taskId: string }>({
      query: ({ taskId }) => ({
        url: ApiPath.PARTNERS + `/getReward/${taskId}`,
        method: 'POST',
      }),
    }),
    airdrop: builder.query<Airdrop, void>({
      query: () => ({
        url: ApiPath.AIRDROP,
      }),
    }),
    refillEnergy: builder.mutation<EnergyRefillResponseType, void>({
      query: () => ({
        url: ApiPath.ENERGY_REFILL,
        method: 'POST',
      }),
    }),
    getVipInfo: builder.query<VipInfoResponseType, void>({
      query: () => ApiPath.VIP_INFO,
      keepUnusedDataFor: 0,
    }),
    activateVipStatus: builder.mutation<
      ActivateVipStatusResponseType | ActivateVipStatusBuyLinkResponseType,
      ActiveteVipStatusRequestType
    >({
      query: (body) => ({
        url: ApiPath.VIP_ACTIVATE,
        method: 'POST',
        body,
      }),
    }),
    claimDailyTgemBonus: builder.mutation<
      ClaimDailyTgemBonuseResponseType,
      ClaimDailyTgemBonuseRequestType
    >({
      query: (body) => ({
        url: ApiPath.DAILY_TGEM_BONUS,
        method: 'POST',
        body,
      }),
    }),
    getUserPartners: builder.query<
      GetUserPartnersResponseType,
      GetUserPartnersRequestType
    >({
      query: ({ page, limit, level }) => ({
        url: ApiPath.REFERRAL_PARTNERS,
        params: { page, limit, level },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-level-${queryArgs.level || 1}`,
      merge: (currentCache, newItems) => {
        currentCache = {
          ...newItems,
          partners: currentCache.partners.concat(newItems.partners),
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
    }),
    getTonTransactionList: builder.query<
      GetTonTransactionsResponseType,
      PaginationRequestType
    >({
      query: ({ page, limit }) => ({
        url: ApiPath.TON_TRANSACTIONS,
        params: { page, limit },
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems) => {
        currentCache = {
          ...newItems,
          transactions: currentCache.transactions.concat(newItems.transactions),
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
    }),
    claimThankYouReward: builder.mutation<
      void,
      ThankForPurchaseRequestResponseType
    >({
      query: (data) => ({
        url: ApiPath.THANK_YOU_CLAIM,
        method: 'POST',
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});
const {
  useGetUserTasksQuery,
  useClaimTaskByIdMutation,
  useGetReferralEarningsQuery,
  useGetUserInviteFriendsListQuery,
  useWithdrawHoldedMoneyMutation,
  useChangeAdditionalParamsMutation,
  useAirdropQuery,
  useRefillEnergyMutation,
  useGetVipInfoQuery,
  useActivateVipStatusMutation,
  useClaimDailyTgemBonusMutation,
  useGetPartnersTaskQuery,
  useCompletePartnerTaskMutation,
  useGetPartnerTaskRewardMutation,
  useAuthTelegramMutation,
  useExtendedInfoQuery,
  useLazyExtendedInfoQuery,
  useGetInitialRewardsMutation,
  useGetOnboardingRewardsMutation,
  useGetUserPartnersQuery,
  useGetTonTransactionListQuery,
  useGetMyInfoMutation,
  useClaimThankYouRewardMutation,
  useGetUserDecorationsQuery,
  useSelectUserDecorationMutation,
} = userApi;

export const useGetUserPartnersQueryState =
  userApi.endpoints.getUserPartners.useQueryState;
export {
  useActivateVipStatusMutation,
  useAirdropQuery,
  useAuthTelegramMutation,
  useChangeAdditionalParamsMutation,
  useClaimDailyTgemBonusMutation,
  useClaimTaskByIdMutation,
  useClaimThankYouRewardMutation,
  useCompletePartnerTaskMutation,
  useExtendedInfoQuery,
  useGetInitialRewardsMutation,
  useGetMyInfoMutation,
  useGetOnboardingRewardsMutation,
  useGetPartnersTaskQuery,
  useGetPartnerTaskRewardMutation,
  useGetReferralEarningsQuery,
  useGetTonTransactionListQuery,
  useGetUserDecorationsQuery,
  useGetUserInviteFriendsListQuery,
  useGetUserPartnersQuery,
  useGetUserTasksQuery,
  useGetVipInfoQuery,
  useLazyExtendedInfoQuery,
  userApi,
  useRefillEnergyMutation,
  useSelectUserDecorationMutation,
  useWithdrawHoldedMoneyMutation,
};
